/**
 * Jyotish Tarang — Login Page v2.5
 * Google OAuth | Facebook OAuth | Email OTP
 * Sacred Ivory Saffron theme | Testimonial carousel
 * Desktop: 2 testimonials left + login card + 2 testimonials right
 * Mobile: login card first, testimonials below (1 at a time)
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = '/api';

function TestimonialSingle({
  images, baseUrl, interval, startOffset = 0
}: {
  images: string[], baseUrl: string, interval: number, startOffset?: number
}) {
  const [idx, setIdx] = useState(startOffset);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (images.length <= 1 || paused) return;
    const t = setTimeout(() => setIdx(i => (i + 1) % images.length), interval);
    return () => clearTimeout(t);
  }, [idx, images.length, paused, interval]);
  if (!images.length) return null;
  return (
    <div
      style={{ width: 375, flexShrink: 0 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <img
        src={baseUrl + images[idx]}
        alt="User testimonial"
        loading="lazy"
        style={{ width: 375, height: 'auto', display: 'block' }}
      />
    </div>
  );
}

function MobileTestimonials({
  images, baseUrl
}: { images: string[], baseUrl: string }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (images.length <= 1 || paused) return;
    const t = setTimeout(() => setIdx(i => (i + 1) % images.length), 4000);
    return () => clearTimeout(t);
  }, [idx, images.length, paused]);

  if (!images.length) return null;

  return (
    <div style={{ width: '100%', maxWidth: 420, margin: '0 auto',
                  paddingTop: 16 }}
         onTouchStart={() => setPaused(true)}
         onTouchEnd={() => setPaused(false)}>
      <div style={{
        borderRadius: 10, overflow: 'hidden',
        border: '0.5px solid var(--parch2)',
        boxShadow: '0 2px 8px rgba(58,46,26,0.08)',
        background: 'var(--cream)', lineHeight: 0,
      }}>
        <img
          src={baseUrl + images[idx]}
          alt="User testimonial"
          loading="lazy"
          style={{ width: '100%', display: 'block' }}
        />
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { t } = useTranslation();
  const [mode, setMode]       = useState<'choose'|'otp-email'|'otp-code'>('choose');
  const [email, setEmail]     = useState('');
  const [otp, setOtp]         = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');
  const [images, setImages]   = useState<string[]>([]);
  const [baseUrl, setBaseUrl] = useState('/marketing/');
  const [isMobile, setIsMobile] = useState(false);

  // Handle OAuth callback — exchange one-time code for tokens via POST
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    if (authCode) {
      // Clear code from URL immediately to prevent bookmark/history leakage
      window.history.replaceState({}, '', '/auth/callback');
      axios.post('/api/auth/exchange', { code: authCode })
        .then(r => {
          if (r.data.access_token) {
            sessionStorage.setItem('access_token', r.data.access_token);
            if (r.data.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
            window.location.href = '/dashboard';
          }
        })
        .catch(() => setError(t('login.errors.signInFailed')));
      return;
    }
    // Legacy: handle URL fragment (fallback for any cached redirects)
    const hash = window.location.hash;
    if (hash.includes('access=')) {
      const params = new URLSearchParams(hash.slice(1));
      const access  = params.get('access');
      const refresh = params.get('refresh');
      if (access) {
        sessionStorage.setItem('access_token', access);
        if (refresh) localStorage.setItem('refresh_token', refresh);
        window.location.href = '/dashboard';
      }
    }
    const err = urlParams.get('error');
    if (err) {
      const msgs: Record<string,string> = {
        google_denied:  t('login.errors.googleDenied'),
        fb_denied:      t('login.errors.fbDenied'),
        fb_no_email:    t('login.errors.fbNoEmail'),
        google_token:   t('login.errors.googleTokenFailed'),
        invalid_state:  t('login.errors.sessionExpired'),
        // 15 Aug 2026: 'suspended' was already redirected here by
        // google_callback() but had no entry in this map, so it silently
        // fell through to the generic signInFailed message. facebook_callback()
        // didn't even redirect here before this same fix -- see auth.py.
        suspended:      t('login.errors.accountSuspended'),
      };
      setError(msgs[err] || t('login.errors.signInFailed'));
    }
  }, [t]);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 900);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Fetch testimonials AFTER login card renders — non-blocking
  useEffect(() => {
    const timer = setTimeout(() => {
      fetch('/api/marketing/testimonials')
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d && d.images && d.images.length > 0) {
            setImages(d.images);
            setBaseUrl(d.base_url || '/marketing/');
          }
        })
        .catch(() => {});
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  async function handleGoogleLogin() {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/auth/google/url`);
      window.location.href = r.data.url;
    } catch { setError(t('login.errors.googleConnectFailed')); setLoading(false); }
  }

  async function handleFacebookLogin() {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/auth/facebook/url`);
      window.location.href = r.data.url;
    } catch { setError(t('login.errors.fbConnectFailed')); setLoading(false); }
  }

  async function handleSendOTP(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes('@')) { setError(t('login.errors.invalidEmail')); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/otp/send`, { email });
      setMode('otp-code');
      setInfo(t('login.otpSentInfo', { email }));
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.errors.otpSendFailed'));
    } finally { setLoading(false); }
  }

  async function handleVerifyOTP(e: React.FormEvent) {
    e.preventDefault();
    if (otp.length !== 4) { setError(t('login.errors.enterCode4')); return; }
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${API}/auth/otp/verify`, { email, otp });
      sessionStorage.setItem('access_token', r.data.access_token);
      if (r.data.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.errors.incorrectOtp'));
    } finally { setLoading(false); }
  }

  // Split images for left and right columns

  const loginCard = (
    <div style={{
      width: '100%', maxWidth: 420,
      background: 'var(--cream)', borderRadius: 'var(--radius-lg)',
      border: '0.5px solid var(--parch2)', boxShadow: 'var(--shadow-md)',
      overflow: 'hidden', flexShrink: 0,
    }}>
      <div style={{
        background: 'var(--sfll)', borderBottom: '2px solid var(--sfb)',
        padding: '28px 24px', textAlign: 'center',
      }}>
        <img src="/static/img/logo.png" alt="Jyotish Tarang"
             style={{ height: 90, marginBottom: 12 }}/>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 20,
                     color: 'var(--indigo)', fontWeight: 500, marginBottom: 4 }}>
          JYOTISH TARANG
        </h1>
        <p style={{ fontSize: 12, color: 'var(--ink3)' }}>
          {t('app.tagline')}
        </p>
      </div>
      <div style={{ padding: '28px 28px 32px' }}>
        {error && <div className="info-box info-red" style={{ marginBottom: 16 }}>{error}</div>}
        {info  && <div className="info-box info-teal" style={{ marginBottom: 16 }}>{info}</div>}
        {mode === 'choose' && (
          <>
            <p style={{ fontSize: 13, color: 'var(--ink2)',
                        textAlign: 'center', marginBottom: 20 }}>
              {t('login.signInPrompt')}
            </p>
            <button className="social-btn" onClick={handleGoogleLogin} disabled={loading}>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t('login.continueGoogle')}
            </button>
            <div style={{ display:'flex',alignItems:'center',gap:10,margin:'16px 0',
                          color:'var(--ink3)',fontSize:12 }}>
              <div style={{flex:1,height:'0.5px',background:'var(--parch2)'}}/>
              {t('login.orUseEmail')}
              <div style={{flex:1,height:'0.5px',background:'var(--parch2)'}}/>
            </div>
            <button className="btn btn-secondary btn-full"
                    onClick={() => setMode('otp-email')} style={{ gap: 8 }}>
              ✉ {t('login.continueEmailOtp')}
            </button>
            <p style={{ fontSize: 11, color: 'var(--ink3)',
                        textAlign: 'center', marginTop: 20, lineHeight: 1.7 }}>
              {t('login.privacyNote')}{' '}
              <a href="/privacy" style={{ color: 'var(--sf)' }}>{t('common.privacyPolicy')}</a>
            </p>
          </>
        )}
        {mode === 'otp-email' && (
          <form onSubmit={handleSendOTP}>
            <div className="form-group">
              <label className="form-label">{t('login.emailAddress')}</label>
              <input className="form-input" type="email" value={email}
                     onChange={e => setEmail(e.target.value)}
                     placeholder="your@email.com" autoFocus required/>
            </div>
            <button className="btn btn-primary btn-full" type="submit" disabled={loading}>
              {loading ? t('login.sending') : t('login.sendOtp')}
            </button>
            <button type="button" className="btn btn-ghost btn-full"
                    style={{ marginTop: 8 }}
                    onClick={() => { setMode('choose'); setError(''); }}>
              ← {t('common.back')}
            </button>
          </form>
        )}
        {mode === 'otp-code' && (
          <form onSubmit={handleVerifyOTP}>
            <p style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 16,
                        textAlign: 'center' }}>
              {t('login.enterCodeSentTo')} <strong>{email}</strong>
            </p>
            <div className="form-group">
              <input className="form-input form-input-mono" type="text"
                     value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,4))}
                     placeholder="● ● ● ●" maxLength={4}
                     style={{ fontSize: 28, letterSpacing: 12, textAlign: 'center' }}
                     autoFocus required/>
              <p className="form-hint" style={{ textAlign: 'center' }}>
                {t('login.codeExpires')}
              </p>
            </div>
            <button className="btn btn-primary btn-full" type="submit"
                    disabled={loading || otp.length !== 4}>
              {loading ? t('login.verifying') : t('login.verifySignIn')}
            </button>
            <button type="button" className="btn btn-ghost btn-full"
                    style={{ marginTop: 8 }}
                    onClick={() => { setMode('otp-email'); setOtp(''); setError(''); setInfo(''); }}>
              ← {t('login.resendCode')}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--ivory)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-start',
        padding: '24px 16px 40px',
      }}>
        {loginCard}
        {images.length > 0 && (
          <MobileTestimonials images={images} baseUrl={baseUrl} />
        )}
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--ivory)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 24px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 8,
        width: '100%', maxWidth: 960,
      }}>
        {images.length > 0 && (
          <TestimonialSingle
            images={images} baseUrl={baseUrl}
            interval={15000}
          />
        )}
        {loginCard}
        {images.length > 0 && (
          <TestimonialSingle
            images={images} baseUrl={baseUrl}
            interval={17000}
            startOffset={Math.floor(images.length / 2)}
          />
        )}
      </div>
    </div>
  );
}
